// 🧰 **모션 부품 상자 — 시간 함수**(2026-09-28 · 모션 스타일 재편 1단계 · B안 «부품 상자 하나»)
//
// ★화면은 시간의 순수 함수다★ — 여기 있는 것은 전부 `(t, …) => 값` 이다. `Math.random`·`Date`·타이머·
//   프레임 사이 상태를 두지 않는다. 그래야 내보내기(mediabunny)·정밀편집 미리보기·카드 사진 굽기가
//   **같은 t 에 같은 픽셀**을 낸다(정본 지도 `docs/01-plan/features/motion-style-pack.md`).
// ★스프링은 «다시 시작»하지 않고 **더한다**(`track`)★ — 목표가 바뀔 때마다 새 스프링을 시작하면
//   그 시각 이전을 전부 계산해야 프레임 812 를 구할 수 있다. 델타를 스프링으로 더하면 어느 프레임이든
//   바로 계산된다(설계서 4절 · Movez 12단계 8절).

export const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

/** `t` 가 `[a, b]` 를 지나는 정도(0~1). `a === b` 면 계단(도달 = 1). */
export function lin(t: number, a: number, b: number): number {
  if (b <= a) return t >= a ? 1 : 0;
  return clamp01((t - a) / (b - a));
}

export const outCubic = (x: number): number => 1 - Math.pow(1 - clamp01(x), 3);

/**
 * 🎚 **CSS `cubic-bezier(x1,y1,x2,y2)` 를 수치로 푼다**(t ↦ 진행도). 뉴턴 반복 8회 + 이분 폴백 — 결정론.
 * ★linear·back·elastic·bounce 는 두지 않는다★(GNWORKS 하네스 흡수 3-1 · 회장님 09-28) — 튀어나감은 아래 `UI_SPRING` 으로만,
 *   그것도 UI 소품(체크 표시·도장 착지)에만. 큰 글자는 튀어나감 없음(연구일지 4절).
 */
export function cubicBezier(x1: number, y1: number, x2: number, y2: number): (t: number) => number {
  const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
  const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
  const sampleX = (u: number) => ((ax * u + bx) * u + cx) * u;
  const sampleY = (u: number) => ((ay * u + by) * u + cy) * u;
  const slopeX = (u: number) => (3 * ax * u + 2 * bx) * u + cx;
  return (t: number) => {
    const x = clamp01(t);
    if (x === 0 || x === 1) return x;
    let u = x;
    for (let i = 0; i < 8; i++) {
      const d = sampleX(u) - x;
      if (Math.abs(d) < 1e-7) return sampleY(u);
      const k = slopeX(u);
      if (Math.abs(k) < 1e-6) break;
      u -= d / k;
    }
    let lo = 0, hi = 1;
    u = x;
    while (hi - lo > 1e-7) {
      u = (lo + hi) / 2;
      if (sampleX(u) < x) lo = u; else hi = u;
    }
    return sampleY(u);
  };
}

/** 이징 토큰(GNWORKS 하네스 3-1 · 공식 material 계열) — 진입·퇴장·이동 셋뿐. */
export const EASE_ENTER = cubicBezier(0.05, 0.7, 0.1, 1);
export const EASE_ENTER_SOFT = cubicBezier(0, 0, 0.3, 1);
export const EASE_EXIT = cubicBezier(0.3, 0, 0.8, 0.15);
export const EASE_MOVE = cubicBezier(0.2, 0, 0, 1);

/**
 * ⏱ **길이 기본값(ms)** — 강조 200~400 · 진입·이동 400~700 · 전체 전환 600~1000 · 단일 트윈 1000 초과 0 · 퇴장 < 진입.
 *   부품은 이 값을 기본으로 받고, 조립기가 컷 길이(초)로 `t01` 단위로 바꾼다(`msToT01`).
 */
export const DURATION_MS = Object.freeze({
  emphasis: 300,
  enter: 500,
  move: 500,
  transition: 800,
  exit: 350,
  /** 한 글자 사이 지연(한 글자씩 등장). */
  perChar: 30,
  /** 단일 트윈 상한 — 넘기면 조립기 버그(테스트가 잰다). */
  maxTween: 1000,
});

/** ms → t01(0~1). 컷 길이를 모르면 조립기가 `KIT_DEFAULT_CUT_SEC` 를 넣는다. */
export const msToT01 = (ms: number, cutSec: number): number => (cutSec > 0 ? ms / 1000 / cutSec : 0);

/** 컷 길이를 못 받은 표면의 기본 가정(초). 사진 굽기(t01=1)는 값과 무관하다. */
export const KIT_DEFAULT_CUT_SEC = 5;

export interface SpringSpec {
  from: number;
  to: number;
  /** 강성(k). 기본 170 — 리모션 `spring()` 기본값과 같은 감(느낌만 · 값은 폐형식으로 직접 푼다). */
  stiffness?: number;
  /** 감쇠(c). 기본 26 — 거의 임계감쇠라 튀어나감이 아주 작다(UI 용). */
  damping?: number;
  mass?: number;
}

/**
 * 감쇠 스프링의 **폐형식 해** — 시각 `τ`(초) 에서 0→1 진행도. `τ <= 0` 이면 0, 충분히 지나면 1.
 * 과감쇠·임계·저감쇠 세 경우를 모두 다룬다(테스트가 셋 다 잰다).
 */
export function springStep(tau: number, stiffness = 170, damping = 26, mass = 1): number {
  if (!(tau > 0)) return 0;
  const k = Math.max(1e-6, stiffness);
  const m = Math.max(1e-6, mass);
  const c = Math.max(0, damping);
  const w0 = Math.sqrt(k / m);
  const zeta = c / (2 * Math.sqrt(k * m));
  let x: number;
  if (zeta < 1 - 1e-9) {
    const wd = w0 * Math.sqrt(1 - zeta * zeta);
    x = 1 - Math.exp(-zeta * w0 * tau) * (Math.cos(wd * tau) + ((zeta * w0) / wd) * Math.sin(wd * tau));
  } else if (zeta < 1 + 1e-9) {
    x = 1 - Math.exp(-w0 * tau) * (1 + w0 * tau);
  } else {
    const s1 = -w0 * (zeta - Math.sqrt(zeta * zeta - 1));
    const s2 = -w0 * (zeta + Math.sqrt(zeta * zeta - 1));
    x = 1 - (s2 * Math.exp(s1 * tau) - s1 * Math.exp(s2 * tau)) / (s2 - s1);
  }
  // 수치 오차·저감쇠 진동은 그대로 두되(그게 «튀어나감»이다) 1e-9 안의 잔차는 접는다.
  return Math.abs(x - 1) < 1e-9 ? 1 : x;
}

/**
 * 🧷 **UI 소품 스프링 프리셋** — f = 2.4Hz · ζ = 0.42 (GNWORKS 하네스 `tkit.js` 공식 재구현 · 코드 복사 아님).
 *   ω0 = 2π·2.4 ≈ 15.08 → k = ω0² ≈ 227.4 · c = 2ζω0 ≈ 12.67 (m = 1). 체크 표시·도장 착지에만 쓴다.
 */
export const UI_SPRING = Object.freeze({ stiffness: (2 * Math.PI * 2.4) ** 2, damping: 2 * 0.42 * 2 * Math.PI * 2.4, mass: 1 });

/** `from` 에서 `to` 로 가는 스프링 값. `tau` 는 스프링이 시작한 뒤 지난 시간. */
export function spring(tau: number, spec: SpringSpec): number {
  return spec.from + (spec.to - spec.from) * springStep(tau, spec.stiffness, spec.damping, spec.mass);
}

export interface TrackKey {
  /** 이 목표가 «시작되는» 시각. */
  t: number;
  v: number;
}

/**
 * 🎯 **목표가 바뀔 때마다 스프링을 더한다.** 키 `[{t:0,v:a},{t:1,v:b},{t:2,v:c}]` 는
 *   a + (b−a)·step(t−1) + (c−b)·step(t−2) — 어느 `t` 든 앞 프레임 없이 바로 값이 나온다.
 * ★키는 시각 오름차순이어야 한다★(정렬하지 않는다 — 정렬은 곧 «상태»다. 어긋난 입력은 테스트가 잡는다).
 * 첫 키의 시각 이전이면 첫 값 그대로다.
 */
export function track(t: number, keys: readonly TrackKey[], spec?: Omit<SpringSpec, "from" | "to">): number {
  if (keys.length === 0) return 0;
  let v = keys[0].v;
  for (let i = 1; i < keys.length; i++) {
    const delta = keys[i].v - keys[i - 1].v;
    if (delta !== 0) v += delta * springStep(t - keys[i].t, spec?.stiffness, spec?.damping, spec?.mass);
  }
  return v;
}

/**
 * 🪟 **창 알파** — `a` 에서 `fade` 동안 켜지고 `b` 에서 `fade` 동안 꺼진다. 창 밖은 0.
 *   `b` 가 없으면 끝나지 않는다(켜진 채 유지).
 */
export function win(t: number, a: number, b?: number, fade = 0.08): number {
  const on = lin(t, a, a + fade);
  if (b === undefined) return on;
  const off = 1 - lin(t, b - fade, b);
  return Math.min(on, off);
}

/**
 * 🎲 **시드 난수**(mulberry32) — 같은 시드면 같은 수열. 부품이 «흔들림» 같은 결을 넣을 때 쓴다.
 *   ★`Math.random` 을 이 상자 안에서 부르지 않는다★(grep 으로 잠근다).
 */
export function seeded(seed: number): () => number {
  let a = (seed >>> 0) || 0x9e3779b9;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let x = a;
    x = Math.imul(x ^ (x >>> 15), x | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * 🥁 **가짜 박자** — M 컷엔 음악 정보가 없어(설계서 안착 인계서 2절) 컷 길이를 균등 분할한다.
 *   `[start, end]` 구간을 `n` 칸으로 나눈 각 칸의 시작 시각. `n <= 0` 이면 빈 배열.
 *   박자 동기(다운비트 실측)는 2단계 — 이 함수의 서명(`t` 목록을 돌려준다)은 그때도 그대로다.
 */
export function beatGrid(n: number, start: number, end: number): number[] {
  if (!(n > 0)) return [];
  const step = (end - start) / n;
  const out: number[] = [];
  for (let i = 0; i < n; i++) out.push(start + step * i);
  return out;
}
